/**
 * Fair MA Assignment Module
 *
 * Implements constraint-based fair assignment of Medical Assistants
 * with the following priority hierarchy:
 * 1. Language Support (HARD constraint - must be respected)
 * 2. Daily Capacity Limit (SOFT constraint - avoid exceeding unless necessary)
 * 3. Weekly Fairness Ratios (OPTIMIZATION - maintain target distributions)
 */
import { Language, MedicalAssistant } from "~/api/API";
import { DEFAULT_DAILY_MA_LIMIT } from "./autoAssignMAConstants";

/**
 * Represents appointment data for weekly analysis
 */
export interface WeeklyAppointmentData {
  weekStart: Date; // Sunday
  weekEnd: Date; // Saturday
  appointments: Array<{
    date: string; // ISO date string (YYYY-MM-DD)
    medicalAssistantId: string;
    patientLanguage: Language;
  }>;
  // Cached aggregations for performance
  dailyCounts: Map<string, Map<string, number>>; // date -> maId -> count
  weeklyCounts: Map<string, number>; // maId -> total count
  languageCounts: Map<string, Map<Language, number>>; // maId -> language -> count
}

/**
 * Context for MA selection decisions
 */
export interface MASelectionContext {
  eligibleMAs: MedicalAssistant[]; // Already filtered by language
  appointmentDate: Date;
  weeklyData: WeeklyAppointmentData;
  language: Language;
  dailyLimit?: number; // Default: DEFAULT_DAILY_MA_LIMIT
}

/**
 * Result of fairness validation
 */
export interface FairnessValidation {
  isValid: boolean;
  violations: string[]; // Critical issues that break constraints
  warnings: string[]; // Non-critical issues (e.g., ratio deviations)
  stats: {
    dailyDistribution: Map<string, Map<string, number>>;
    weeklyDistribution: Map<string, number>;
    languageDistribution: Map<string, Map<Language, number>>;
  };
}

/**
 * Main fair selection function that respects constraint hierarchy
 */
export function selectMAFairly(context: MASelectionContext): string | null {
  const {
    eligibleMAs,
    appointmentDate,
    weeklyData,
    language,
    dailyLimit = DEFAULT_DAILY_MA_LIMIT,
  } = context;

  // Priority 1: Language constraint
  // If only one MA available for language, return immediately
  if (eligibleMAs.length === 0) {
    return null;
  }

  if (eligibleMAs.length === 1) {
    return eligibleMAs[0].id;
  }

  // Get daily counts for the specific date (timezone-neutral)
  const dateStr = `${appointmentDate.getFullYear()}-${String(appointmentDate.getMonth() + 1).padStart(2, "0")}-${String(appointmentDate.getDate()).padStart(2, "0")}`;
  const dailyCounts =
    weeklyData.dailyCounts.get(dateStr) || new Map<string, number>();

  // Priority 2: Daily capacity limit
  // Separate MAs by daily limit status
  const masUnderLimit = eligibleMAs.filter(
    (ma) => (dailyCounts.get(ma.id) || 0) < dailyLimit,
  );
  const masAtOrOverLimit = eligibleMAs.filter(
    (ma) => (dailyCounts.get(ma.id) || 0) >= dailyLimit,
  );

  // Prefer MAs under daily limit
  if (masUnderLimit.length > 0) {
    // Priority 3: Weekly fairness optimization
    return selectByWeeklyFairness(masUnderLimit, weeklyData, language);
  }

  // All eligible MAs are at/over daily limit
  // Select the one with lowest daily count to distribute load
  if (masAtOrOverLimit.length > 0) {
    return selectByLowestDailyCount(masAtOrOverLimit, dailyCounts);
  }

  return null;
}

/**
 * Selects MA based on weekly fairness - chooses MA furthest below target ratio
 */
function selectByWeeklyFairness(
  eligibleMAs: MedicalAssistant[],
  weeklyData: WeeklyAppointmentData,
  language: Language,
): string {
  // Calculate total appointments for this language
  const languageAppointments = weeklyData.appointments.filter(
    (a) => a.patientLanguage === language,
  );
  const totalLanguageAppointments = languageAppointments.length;

  // Calculate each MA's deviation from target ratio
  const deviations = eligibleMAs.map((ma) => {
    // Get target weight for this language
    const languageWeight = ma.assignmentCriteria?.languageWeights?.find(
      (lw) => lw.language === language,
    );
    const targetWeight = languageWeight?.weight || 1.0;

    // Calculate total weight across eligible MAs for normalization
    const totalWeight = eligibleMAs.reduce((sum, m) => {
      const weight =
        m.assignmentCriteria?.languageWeights?.find(
          (lw) => lw.language === language,
        )?.weight || 1.0;
      return sum + weight;
    }, 0);

    // Target ratio for this MA
    const targetRatio = totalWeight > 0 ? targetWeight / totalWeight : 0;

    // Actual current ratio
    let actualCount = 0;
    if (totalLanguageAppointments > 0) {
      actualCount = languageAppointments.filter(
        (a) => a.medicalAssistantId === ma.id,
      ).length;
    }
    const actualRatio =
      totalLanguageAppointments > 0
        ? actualCount / (totalLanguageAppointments + 1) // +1 for the appointment we're about to assign
        : 0;

    // Deviation (positive means under-assigned, negative means over-assigned)
    const deviation = targetRatio - actualRatio;

    return {
      ma,
      targetRatio,
      actualRatio,
      deviation,
      actualCount,
    };
  });

  // Sort by deviation (highest first - most under-assigned)
  // In case of tie, prefer MA with fewer total appointments
  deviations.sort((a, b) => {
    const deviationDiff = b.deviation - a.deviation;
    if (Math.abs(deviationDiff) > 0.001) {
      return deviationDiff;
    }
    // Tie breaker: prefer MA with fewer appointments
    return a.actualCount - b.actualCount;
  });

  // Select MA with highest deviation (most under-assigned)
  return deviations[0].ma.id;
}

/**
 * Selects MA with lowest daily count when all are at/over limit
 */
function selectByLowestDailyCount(
  eligibleMAs: MedicalAssistant[],
  dailyCounts: Map<string, number>,
): string {
  // Find MA with minimum daily appointments
  let selectedMA = eligibleMAs[0];
  let minCount = dailyCounts.get(selectedMA.id) || 0;

  for (const ma of eligibleMAs.slice(1)) {
    const count = dailyCounts.get(ma.id) || 0;
    if (count < minCount) {
      minCount = count;
      selectedMA = ma;
    }
  }

  return selectedMA.id;
}

/**
 * Gets the Sunday-Saturday week boundaries for a given date
 * Sunday through Saturday are grouped in the same logical week
 */
export function getLogicalWeek(date: Date): { weekStart: Date; weekEnd: Date } {
  const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday

  // Find the Sunday that starts this week
  const weekStart = new Date(date);
  weekStart.setDate(weekStart.getDate() - dayOfWeek);
  weekStart.setHours(0, 0, 0, 0);

  // Find the Saturday that ends this week (6 days after Sunday)
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  return { weekStart, weekEnd };
}

/**
 * Parses a date string (YYYY-MM-DD) into a Date object in local timezone
 * Avoids timezone conversion issues that can shift the day
 */
export function parseLocalDate(
  dateInput: string | Date | { value: string } | { toString(): string },
): Date {
  // If already a Date object, return as-is
  if (dateInput instanceof Date) {
    return dateInput;
  }

  let dateString: string;

  // Handle DateISO objects with value property
  if (
    typeof dateInput === "object" &&
    dateInput !== null &&
    "value" in dateInput
  ) {
    dateString = (dateInput as { value: string }).value;
  } else if (
    typeof dateInput === "object" &&
    dateInput !== null &&
    "toString" in dateInput
  ) {
    // Handle DateISO class instances that have toString() method
    dateString = dateInput.toString();
  } else if (typeof dateInput === "string") {
    dateString = dateInput;
  } else {
    throw new Error(
      `parseLocalDate expects string, Date, or DateISO, got ${typeof dateInput}`,
    );
  }

  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
}

/**
 * Formats date range for GraphQL queries
 */
export function formatDateRangeForQuery(
  weekStart: Date,
  weekEnd: Date,
): { startDate: string; endDate: string } {
  // Format as YYYY-MM-DD for GraphQL
  const startDate = weekStart.toISOString().split("T")[0];
  const endDate = weekEnd.toISOString().split("T")[0];
  return { startDate, endDate };
}
